/**
 * Algolia Recommend — Simple Lighting
 *
 * Three widgets rendered via the Algolia Recommend REST API (no CDN library required):
 *   1. Frequently Bought Together  — [data-algolia-fbt]
 *   2. Related Products            — [data-algolia-related]
 *   3. Recommended For You         — [data-algolia-recommended]  (homepage)
 *
 * Also tracks recently-viewed products into localStorage key `sl_recently_viewed`.
 *
 * Reads window.SlAlgoliaConfig for { appId, apiKey, indexName }.
 * Loaded with defer — never blocks rendering.
 * All API calls wrapped in try/catch — never breaks the page.
 */
(function () {
  'use strict';

  /* ══════════════════════════════════════════════
     GUARD: abort if config is missing
     ══════════════════════════════════════════════ */

  if (!window.SlAlgoliaConfig ||
      !window.SlAlgoliaConfig.appId ||
      !window.SlAlgoliaConfig.apiKey ||
      !window.SlAlgoliaConfig.indexName) {
    return;
  }

  var config    = window.SlAlgoliaConfig;
  var APP_ID    = config.appId;
  var API_KEY   = config.apiKey;
  var INDEX     = config.indexName;

  /* ══════════════════════════════════════════════
     HELPERS
     ══════════════════════════════════════════════ */

  function esc(s) {
    var d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }

  function vatMode() {
    try { return localStorage.getItem('vatPreference') || 'inc'; } catch(e) { return 'inc'; }
  }

  function vatLabel() {
    return vatMode() === 'ex' ? 'EX VAT' : 'INC VAT';
  }

  function fmtMoney(cents) {
    var exVat = vatMode() === 'ex';
    var display = exVat ? Math.round(cents / 1.2) : cents;
    return '\u00A3' + (display / 100).toFixed(2);
  }

  /* Cheapest single-unit variant price for the customer */
  function getCheapestPrice(hit) {
    if (window.__b2bTier && hit.meta && hit.meta.custom && hit.meta.custom.b2b_prices) {
      var b2bPrices = hit.meta.custom.b2b_prices;
      var cheapest = Infinity;
      for (var vid in b2bPrices) {
        if (b2bPrices.hasOwnProperty(vid) && b2bPrices[vid][window.__b2bTier] !== undefined) {
          var vp = b2bPrices[vid][window.__b2bTier];
          if (vp < cheapest) cheapest = vp;
        }
      }
      return (cheapest < Infinity) ? cheapest : (hit.variants_min_price || hit.price);
    }
    return hit.variants_min_price || hit.price;
  }

  /* ══════════════════════════════════════════════
     RECENTLY VIEWED — localStorage tracking
     ══════════════════════════════════════════════ */

  var RECENTLY_VIEWED_KEY = 'sl_recently_viewed';
  var RECENTLY_VIEWED_MAX = 10;

  function loadRecentlyViewed() {
    try {
      var raw = localStorage.getItem(RECENTLY_VIEWED_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch(e) {
      return [];
    }
  }

  function saveRecentlyViewed(items) {
    try {
      localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(items));
    } catch(e) { /* silent — storage full or blocked */ }
  }

  function trackRecentlyViewed(objectID) {
    if (!objectID) return;
    var id    = String(objectID);
    var items = loadRecentlyViewed();
    items = items.filter(function(i) { return i !== id; });
    items.unshift(id);
    if (items.length > RECENTLY_VIEWED_MAX) {
      items = items.slice(0, RECENTLY_VIEWED_MAX);
    }
    saveRecentlyViewed(items);
  }

  /* ══════════════════════════════════════════════
     ALGOLIA RECOMMEND REST API
     ══════════════════════════════════════════════ */

  var RECOMMEND_ENDPOINT = 'https://' + APP_ID + '.algolia.net/1/indexes/*/recommendations';

  function fetchRecommendations(requests) {
    var payload = { requests: requests };
    return fetch(RECOMMEND_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Algolia-Application-Id': APP_ID,
        'X-Algolia-API-Key': API_KEY
      },
      body: JSON.stringify(payload)
    })
    .then(function(r) {
      if (!r.ok) {
        return r.text().then(function(body) {
          // Suppress only "model not trained yet" 404s (Algolia returns body containing
          // "does not exist"). All other errors still log + throw.
          if (!(r.status === 404 && body.indexOf('does not exist') !== -1)) console.error('[Algolia Recommend] HTTP ' + r.status, body);
          throw new Error('Algolia Recommend HTTP ' + r.status);
        });
      }
      return r.json();
    })
    .then(function(data) {
      if (!data || !Array.isArray(data.results)) return [];
      var all = [];
      data.results.forEach(function(result) {
        if (result && Array.isArray(result.hits)) {
          all = all.concat(result.hits);
        }
      });
      return all;
    });
  }

  /* ══════════════════════════════════════════════
     REVIEWS.IO RE-TRIGGER
     ══════════════════════════════════════════════ */

  function triggerReviewsIo() {
    if (typeof ratingSnippet === 'function') {
      try {
        ratingSnippet('ruk_rating_snippet', {
          store: 'simple-lighting',
          color: '#F5A523',
          linebreak: false,
          text: 'Reviews',
          showEmptyStars: true
        });
      } catch(e) { /* silent */ }
    }
  }

  /* ══════════════════════════════════════════════
     CARD RENDERER — identical to cpg-card pattern
     ══════════════════════════════════════════════ */

  function renderCard(hit, idx) {
    var handle        = hit.handle || '';
    var url           = '/products/' + esc(handle);
    var img           = hit.product_image || hit.image || '';
    var title         = hit.title || '';
    var displayPrice  = getCheapestPrice(hit);
    var priceCents    = Math.round((displayPrice || 0) * 100);
    var compareCents  = 0;
    if (!window.__b2bTier) {
      var minP = hit.variants_min_price || hit.price;
      var maxP = hit.variants_max_price || hit.price;
      if (minP >= maxP) {
        compareCents = Math.round((hit.compare_at_price || 0) * 100);
      }
    }
    var hasSale       = compareCents > priceCents && compareCents > 0;
    var variantsCount = hit.variants_count || hit.variant_count || 1;
    var parentSku     = (hit.meta && hit.meta.custom && hit.meta.custom.parent_sku) ? hit.meta.custom.parent_sku : '';
    var allVariantSkus = (hit.meta && hit.meta.custom && hit.meta.custom.all_variant_skus) ? hit.meta.custom.all_variant_skus : (hit.sku || '');
    var sku           = parentSku ? parentSku + ';' + allVariantSkus : allVariantSkus;

    var html = '<li class="cpg-grid__item">';
    html += '<a href="' + url + '" class="cpg-card" data-object-id="' + esc(String(hit.objectID || '')) + '" data-position="' + (idx + 1) + '" aria-label="' + esc(title) + '">';
    html += '<div class="cpg-card__media">';
    if (img) {
      html += '<img src="' + esc(img) + '" alt="' + esc(title) + '" loading="lazy">';
    } else {
      html += '<div class="cpg-card__placeholder"><span>No image</span></div>';
    }
    html += '<button type="button" class="quick-add-btn" data-quick-add data-handle="' + esc(handle) + '" data-variants-count="' + variantsCount + '"' + (parentSku ? ' data-parent-sku="' + esc(parentSku) + '"' : '') + ' aria-label="Quick add ' + esc(title) + '">';
    html += '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>';
    html += '</button>';
    html += '</div>';
    html += '<div class="cpg-card__info">';
    html += '<p class="cpg-card__title">' + esc(title) + '</p>';
    if (sku) {
      html += '<div class="ruk_rating_snippet" data-sku="' + esc(sku) + '" style="min-height:16px;"></div>';
    }
    html += '<div class="cpg-card__price">';
    if (hasSale) {
      html += '<span class="cpg-card__price-sale" data-price-inc="' + priceCents + '">' + fmtMoney(priceCents) + '</span>';
      html += '<span class="cpg-card__price-was" data-compare-inc="' + compareCents + '">Was ' + fmtMoney(compareCents) + '</span>';
    } else {
      html += '<span class="cpg-card__price-regular" data-price-inc="' + priceCents + '">' + fmtMoney(priceCents) + '</span>';
    }
    html += '</div>';
    html += '<span class="cpg-card__vat" data-vat-badge>' + vatLabel() + '</span>';
    html += '</div>';
    html += '</a></li>';
    return html;
  }

  /* ══════════════════════════════════════════════
     CLICK TRACKING (delegated, for Related & Recommended)
     ══════════════════════════════════════════════ */

  function bindBrowseClickTracking(container) {
    container.addEventListener('click', function(ev) {
      var card = ev.target.closest('.cpg-card');
      if (!card) return;
      var objectID = card.dataset.objectId;
      if (!objectID) return;
      try {
        if (window.SlAlgoliaEvents) {
          window.SlAlgoliaEvents.trackBrowseClick(objectID);
        }
      } catch(e) { /* silent */ }
    });
  }

  /* ══════════════════════════════════════════════
     SECTION HEADING RENDERER
     ══════════════════════════════════════════════ */

  function renderHeading(text) {
    return '<p class="sl-recommend__heading">' + esc(text) + '</p>';
  }

  /* ══════════════════════════════════════════════
     WIDGET 1 — FREQUENTLY BOUGHT TOGETHER
     ══════════════════════════════════════════════ */

  function initFbt() {
    var el = document.querySelector('[data-algolia-fbt]');
    if (!el) return;

    var objectID = el.dataset.variantId;
    if (!objectID) return;

    fetchRecommendations([{
      indexName: INDEX,
      objectID: String(objectID),
      model: 'bought-together',
      threshold: 0,
      maxRecommendations: 6,
      /* Hex configurator components/parent carry tags:do-not-display (spec 2.2)
         so they never surface in Frequently Bought Together. */
      queryParameters: { filters: 'NOT tags:"do-not-display"' }
    }])
    .then(function(hits) {
      if (!hits || hits.length < 2) return;

      var cardsHtml = '';
      hits.forEach(function(hit, idx) {
        cardsHtml += renderCard(hit, idx);
      });

      el.innerHTML =
        renderHeading('Frequently Bought Together') +
        '<ul class="cpg-grid sl-recommend__grid" role="list">' + cardsHtml + '</ul>';

      el.removeAttribute('hidden');

      bindBrowseClickTracking(el);
      /* Algolia Insights: FBT viewed */
      try {
        if (window.SlAlgoliaEvents && window.SlAlgoliaEvents.trackProductsViewed) {
          window.SlAlgoliaEvents.trackProductsViewed(
            hits.map(function(h) { return h.objectID; }),
            'FBT Viewed'
          );
        }
      } catch(e) { /* silent */ }
      triggerReviewsIo();
    })
    .catch(function() { /* silent — FBT is non-critical */ });
  }

  /* ══════════════════════════════════════════════
     WIDGET 2 — RELATED PRODUCTS
     ══════════════════════════════════════════════ */

  function initRelated() {
    var el = document.querySelector('[data-algolia-related]');
    if (!el) return;

    var objectID = el.dataset.variantId;
    if (!objectID) return;

    fetchRecommendations([{
      indexName: INDEX,
      objectID: String(objectID),
      model: 'related-products',
      threshold: 0,
      maxRecommendations: 4,
      /* Hex configurator components/parent carry tags:do-not-display (spec 2.2)
         so they never surface in Related Products. */
      queryParameters: { filters: 'NOT tags:"do-not-display"' }
    }])
    .then(function(hits) {
      if (!hits || hits.length < 3) return;

      var cardsHtml = '';
      hits.slice(0, 4).forEach(function(hit, idx) {
        cardsHtml += renderCard(hit, idx);
      });

      el.innerHTML =
        renderHeading('You Might Also Like') +
        '<ul class="cpg-grid sl-recommend__grid" role="list">' + cardsHtml + '</ul>';

      el.removeAttribute('hidden');

      /* Algolia Insights: Related Products viewed */
      try {
        if (window.SlAlgoliaEvents && window.SlAlgoliaEvents.trackProductsViewed) {
          window.SlAlgoliaEvents.trackProductsViewed(
            hits.map(function(h) { return h.objectID; }),
            'Related Products Viewed'
          );
        }
      } catch(e) { /* silent */ }

      bindBrowseClickTracking(el);
      triggerReviewsIo();
    })
    .catch(function() { /* silent */ });
  }

  /* ══════════════════════════════════════════════
     WIDGET 3 — RECOMMENDED FOR YOU (Homepage)
     ══════════════════════════════════════════════ */

  function initRecommended() {
    var el = document.querySelector('[data-algolia-recommended]');
    if (!el) return;

    var recentlyViewed = loadRecentlyViewed();
    var isPersonalized = recentlyViewed.length > 0;

    var requests;

    if (isPersonalized) {
      var seeds = recentlyViewed.slice(0, 3);
      requests = seeds.map(function(id) {
        return {
          indexName: INDEX,
          objectID: String(id),
          model: 'related-products',
          threshold: 0,
          maxRecommendations: 4,
          /* Hex configurator components/parent carry tags:do-not-display (spec 2.2)
             so they never surface in Recommended For You. */
          queryParameters: { filters: 'NOT tags:"do-not-display"' }
        };
      });
    } else {
      requests = [{
        indexName: INDEX,
        model: 'trending-items',
        threshold: 0,
        maxRecommendations: 8,
        /* Hex configurator components/parent carry tags:do-not-display (spec 2.2)
           so they never surface in Trending Right Now. */
        queryParameters: { filters: 'NOT tags:"do-not-display"' }
      }];
    }

    fetchRecommendations(requests)
    .then(function(hits) {
      if (!hits || hits.length < 3) return;

      /* Deduplicate and exclude seed products */
      var seen   = {};
      var unique = [];
      hits.forEach(function(hit) {
        var id = hit.objectID;
        if (!id || seen[id]) return;
        seen[id] = true;
        if (isPersonalized && recentlyViewed.indexOf(String(id)) !== -1) return;
        unique.push(hit);
      });

      var displayHits = unique.slice(0, 8);
      if (displayHits.length < 3) return;

      var heading   = isPersonalized ? 'Recommended For You' : 'Trending Right Now';
      var cardsHtml = '';
      displayHits.forEach(function(hit, idx) {
        cardsHtml += renderCard(hit, idx);
      });

      el.innerHTML =
        renderHeading(heading) +
        '<ul class="sl-recommend__carousel" role="list">' + cardsHtml + '</ul>';

      el.removeAttribute('hidden');

      /* Algolia Insights: Recommendations viewed */
      try {
        if (window.SlAlgoliaEvents && window.SlAlgoliaEvents.trackProductsViewed) {
          window.SlAlgoliaEvents.trackProductsViewed(
            displayHits.map(function(h) { return h.objectID; }),
            'Recommendations Viewed'
          );
        }
      } catch(e) { /* silent */ }

      bindBrowseClickTracking(el);
      triggerReviewsIo();
    })
    .catch(function() { /* silent */ });
  }

  /* ══════════════════════════════════════════════
     TRACK RECENTLY VIEWED ON PRODUCT PAGES
     ══════════════════════════════════════════════ */

  function initRecentlyViewedTracking() {
    var productEl = document.querySelector('[data-algolia-fbt][data-variant-id]') ||
                    document.querySelector('[data-algolia-related][data-variant-id]');
    if (!productEl) return;
    var objectID = productEl.dataset.variantId;
    if (objectID) trackRecentlyViewed(objectID);
  }

  /* ══════════════════════════════════════════════
     BOOT
     ══════════════════════════════════════════════ */

  function boot() {
    try { initRecentlyViewedTracking(); } catch(e) { /* silent */ }
    try { initFbt();          } catch(e) { /* silent */ }
    try { initRelated();      } catch(e) { /* silent */ }
    try { initRecommended();  } catch(e) { /* silent */ }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();



